/**
 * 運費歸單(REQ-20260908002)。
 *
 * 流程:選貨運公司 + 起訖日期 → 列出該廠商**未歸單**的運費明細 → 逐列勾選 →
 * 輸入廠商對帳單金額 → 即時顯示「已勾選合計 / 對帳單金額 / 差異」→
 * 系統自動在未勾選的明細裡比對出湊得出差異的組合 → 建立一張 **DRAFT** 進貨單。
 *
 * ⚠️ **建出來的是草稿不是已過帳** —— 這正是本次需求的重點:要人核對過對帳單再入帳。
 * 之後仍走既有的送出 → 確認狀態機(REQ-20260813001 R-08),一步都沒有跳過。
 *
 * ⚠️ **設計稿未取得**:需求提到的「運費歸單畫面.jpg」沒有隨派工附上,所以版面
 * 沒有臆造細節 —— 表格照 `.claude/skills/ui-guidelines` 的標準樣式,篩選列與合計卡
 * 照既有會計頁面(`PayablePanel` / `ReceivablePanel`)的慣例。待使用者比對。
 */
import { useEffect, useMemo, useState } from 'react';
import {
  FREIGHT_PAYMENT_TYPES,
  FREIGHT_WORK_ORDER_STATES,
  type FreightLineItem,
  type FreightPaymentType,
  type FreightWorkOrderState,
} from '@app/shared';
import { getApiErrorMessage } from '../../lib/api-client';
import { formatMoney } from '../../lib/format-money';
import { useSupplier } from '../suppliers/api';
import { SupplierSearchModal } from '../suppliers/SupplierSearchModal';
import { useConsolidateFreightLines, useFreightLines, usePatchFreightLine } from './api';
import { matchDifference } from './difference-match';

export function FreightConsolidationPanel(): JSX.Element {
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [vendorPickerOpen, setVendorPickerOpen] = useState(false);
  const [from, setFrom] = useState(() => `${currentPeriodInTaipei()}-01`);
  const [to, setTo] = useState(todayInTaipei);
  /** 勾選的明細 id。用 Set 而不是陣列 —— 這一頁全部的操作都是「有沒有勾」 */
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [statementNo, setStatementNo] = useState('');
  const [statementAmount, setStatementAmount] = useState('');
  /** 帳單日期 = 進貨單的 txnDate,也決定 A 字軌單號的日期段。預設今天 */
  const [txnDate, setTxnDate] = useState(todayInTaipei);

  const vendorQuery = useSupplier(vendorId ?? undefined);
  const query = useFreightLines({ vendorId: vendorId ?? undefined, from, to });
  const mutation = useConsolidateFreightLines();
  /**
   * 託運單號就地補登(REQ-20260915001)。出貨當下常常還沒拿到單號,留白是正常的,
   * 缺的一直是「之後在哪裡補」—— 後端的 `PATCH /v1/freight-lines/:id` 與這支 hook
   * 早就在了,只差這一格沒接上。
   *
   * ⚠️ **整個畫面共用一個 mutation**:逐列各自 `usePatchFreightLine()` 的話,
   * 前一列的錯誤訊息會被後一列的狀態蓋掉。用 `variables.id` 認出是哪一列在存。
   */
  const patchMutation = usePatchFreightLine();

  const rows = query.data?.items ?? [];
  const pickedRows = rows.filter((r) => picked.has(r.id));

  /**
   * 已勾選合計。**到付不計** —— 那筆錢是收貨方付給貨運行的,不是我方的成本,
   * 進貨單的金額也不含它(`calcPurchaseTotals()` 對 COLLECT 的處理)。
   * 用分運算,避免 0.1 + 0.2 的浮點誤差跑進「差異」那一格。
   */
  const pickedTotal =
    pickedRows.reduce(
      (sum, r) => (r.paymentType === 'COLLECT' ? sum : sum + Math.round(Number(r.amount ?? 0) * 100)),
      0,
    ) / 100;
  const statementNum = Number(statementAmount);
  const hasStatement = statementAmount.trim() !== '' && Number.isFinite(statementNum);
  const difference = hasStatement ? Math.round((statementNum - pickedTotal) * 100) / 100 : 0;

  /** 差異湊得出來的組合(只在未勾選的明細裡找,只做到兩筆 —— 見 difference-match.ts) */
  const matches = useMemo(
    () =>
      hasStatement && difference !== 0
        ? matchDifference(
            difference,
            rows.filter((r) => !picked.has(r.id) && r.paymentType !== 'COLLECT'),
          )
        : [],
    [hasStatement, difference, rows, picked],
  );
  const suggestedIds = new Set(matches.flatMap((m) => m.ids));

  const toggle = (id: number): void => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = (): void => {
    if (vendorId == null) return;
    mutation.mutate(
      {
        vendorId,
        txnDate,
        freightLineIds: [...picked],
        statementNo: statementNo.trim() || null,
        statementAmount: statementAmount.trim() || null,
      },
      {
        onSuccess: () => {
          // 歸完就把勾選與對帳單那幾格清掉 —— 同一家廠商可能還要再歸下一個月
          setPicked(new Set());
          setStatementNo('');
          setStatementAmount('');
        },
      },
    );
  };

  return (
    <div className="grid gap-4">
      {/* ── 篩選:廠商 + 起訖運送日 ───────────────────────────────── */}
      <div className="grid gap-3 md:grid-cols-3">
        <div className="grid gap-1">
          <span className="text-sm text-slate-600">貨運公司 *</span>
          <div className="flex items-center gap-2">
            {vendorId == null ? (
              <span className="text-sm text-slate-500" data-testid="freight-vendor-empty">
                尚未選擇
              </span>
            ) : (
              <span className="text-sm text-slate-900" data-testid="freight-vendor-name">
                {vendorQuery.data?.simpleName ?? `廠商 #${vendorId}`}
              </span>
            )}
            <button
              type="button"
              data-testid="freight-vendor-pick"
              onClick={() => setVendorPickerOpen(true)}
              className="px-2 py-1 text-xs rounded border hover:bg-slate-50"
            >
              {vendorId == null ? '選擇廠商' : '重選'}
            </button>
          </div>
        </div>
        <div className="grid gap-1">
          <label className="text-sm text-slate-600" htmlFor="freight-from">
            運送日起
          </label>
          <input
            id="freight-from"
            type="date"
            data-testid="freight-from"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div className="grid gap-1">
          <label className="text-sm text-slate-600" htmlFor="freight-to">
            運送日迄
          </label>
          <input
            id="freight-to"
            type="date"
            data-testid="freight-to"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm"
          />
        </div>
      </div>

      <SupplierSearchModal
        open={vendorPickerOpen}
        onClose={() => setVendorPickerOpen(false)}
        defaultCategoryName="郵寄運送"
        onPick={(id) => {
          setVendorId(id);
          // 換一家廠商就把上一家的勾選清掉 —— 留著會把 A 家的明細歸到 B 家的單上
          setPicked(new Set());
          setVendorPickerOpen(false);
        }}
      />

      {/* ── 三態:沒選廠商 / 載入 / 錯誤 / 空 ─────────────────────── */}
      {vendorId == null ? (
        <div className="text-slate-500 text-sm">先選一家貨運公司,才列得出它的未歸單明細。</div>
      ) : query.isLoading ? (
        <div role="status" className="text-slate-500 text-sm">
          載入運費明細中...
        </div>
      ) : query.isError ? (
        <div role="alert" className="text-red-600 text-sm">
          載入失敗：{getApiErrorMessage(query.error, '未知錯誤')}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-slate-500 text-sm">
          這段期間沒有未歸單的運費明細。出貨登記選「委外貨運」就會在這裡出現。
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 text-sm">
            <button
              type="button"
              data-testid="freight-select-all"
              onClick={() => setPicked(new Set(rows.map((r) => r.id)))}
              className="px-2 py-1 rounded border hover:bg-slate-50"
            >
              全選
            </button>
            <button
              type="button"
              data-testid="freight-select-none"
              onClick={() => setPicked(new Set())}
              className="px-2 py-1 rounded border hover:bg-slate-50"
            >
              全不選
            </button>
            <span className="text-slate-500">
              共 {rows.length} 筆,未歸單合計 {formatMoney(query.data?.totalAmount)}
              (不含到付)
            </span>
          </div>

          <table className="w-full bg-white shadow-sm rounded-md">
            <thead>
              <tr className="border-b text-left text-sm text-slate-600">
                <th className="px-4 py-2 w-10"> </th>
                <th className="px-4 py-2 whitespace-nowrap">運送日</th>
                <th className="px-4 py-2 whitespace-nowrap">來源出貨單</th>
                <th className="px-4 py-2 whitespace-nowrap">託運單號</th>
                <th className="px-4 py-2">送達客戶</th>
                <th className="px-4 py-2 whitespace-nowrap">付款別</th>
                <th className="px-4 py-2 text-right">金額</th>
                <th className="px-4 py-2">歸屬狀態</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <FreightRow
                  key={r.id}
                  row={r}
                  checked={picked.has(r.id)}
                  suggested={suggestedIds.has(r.id)}
                  onToggle={() => toggle(r.id)}
                  onPatchTrackingNo={(trackingNo) => patchMutation.mutate({ id: r.id, trackingNo })}
                  patchPending={patchMutation.isPending && patchMutation.variables?.id === r.id}
                  patchSuccess={patchMutation.isSuccess && patchMutation.variables?.id === r.id}
                  patchError={
                    patchMutation.isError && patchMutation.variables?.id === r.id
                      ? patchMutation.error
                      : null
                  }
                />
              ))}
            </tbody>
          </table>

          {/* ── 對帳單:單號 / 金額 / 帳單日期 + 差異 ─────────────── */}
          <div className="grid gap-3 md:grid-cols-3 border rounded-md p-3">
            <div className="grid gap-1">
              <label className="text-sm text-slate-600" htmlFor="freight-statement-no">
                對帳單號
              </label>
              <input
                id="freight-statement-no"
                type="text"
                maxLength={30}
                data-testid="freight-statement-no"
                placeholder="廠商月結單號"
                value={statementNo}
                onChange={(e) => setStatementNo(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div className="grid gap-1">
              <label className="text-sm text-slate-600" htmlFor="freight-statement-amount">
                對帳單金額
              </label>
              <input
                id="freight-statement-amount"
                type="text"
                inputMode="decimal"
                data-testid="freight-statement-amount"
                value={statementAmount}
                onChange={(e) => setStatementAmount(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm text-right font-mono"
              />
            </div>
            <div className="grid gap-1">
              <label className="text-sm text-slate-600" htmlFor="freight-txn-date">
                帳單日期 *
              </label>
              <input
                id="freight-txn-date"
                type="date"
                data-testid="freight-txn-date"
                value={txnDate}
                onChange={(e) => setTxnDate(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span>
              已勾選 {picked.size} 筆,合計{' '}
              <span data-testid="freight-picked-total" className="font-mono text-slate-900">
                {formatMoney(pickedTotal)}
              </span>
            </span>
            <span>
              對帳單金額{' '}
              <span data-testid="freight-statement-total" className="font-mono text-slate-900">
                {hasStatement ? formatMoney(statementNum) : '—'}
              </span>
            </span>
            <span>
              差異{' '}
              <span
                data-testid="freight-difference"
                className={`font-mono ${
                  !hasStatement ? 'text-slate-400' : difference === 0 ? 'text-emerald-700' : 'text-red-600'
                }`}
              >
                {hasStatement ? formatMoney(difference) : '—'}
              </span>
            </span>
          </div>

          {/* 差異湊得出來的組合 —— 只在未勾選的明細裡找,而且只做到兩筆 */}
          {matches.length > 0 ? (
            <div
              className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2"
              data-testid="freight-difference-hint"
            >
              差異可能是這幾筆沒勾到:
              {matches.map((m, i) => (
                <span key={m.ids.join('-')}>
                  {i > 0 ? '、' : ' '}
                  {m.ids
                    .map((id) => {
                      const row = rows.find((r) => r.id === id);
                      return row ? `${row.shipDate} ${formatMoney(row.amount)}` : `#${id}`;
                    })
                    .join(' + ')}
                </span>
              ))}
            </div>
          ) : hasStatement && difference !== 0 ? (
            <div className="text-xs text-slate-500" data-testid="freight-difference-nohint">
              未勾選的明細裡找不到(單筆或兩筆相加)剛好等於差異的組合,請自行核對。
            </div>
          ) : null}

          {mutation.isError ? (
            <div role="alert" className="text-red-600 text-sm">
              建立失敗：{getApiErrorMessage(mutation.error, '未知錯誤')}
            </div>
          ) : null}
          {mutation.isSuccess ? (
            <div className="text-emerald-700 text-sm" data-testid="freight-consolidate-done">
              已建立進貨單 {mutation.data.orderNo}(草稿,共 {mutation.data.lineCount} 筆,
              合計 {formatMoney(mutation.data.total)})—— 到「進貨管理」核對後再送出確認。
            </div>
          ) : null}

          <div className="flex items-center justify-between border-t pt-3">
            <p className="text-xs text-slate-500">
              建立出來的是<strong>草稿</strong>,不會直接入帳;核對過廠商的對帳單之後,
              到「進貨管理」按送出確認才會產生應付。
            </p>
            <button
              type="button"
              data-testid="freight-consolidate"
              onClick={submit}
              disabled={picked.size === 0 || !txnDate || mutation.isPending}
              className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? '建立中...' : '建立進貨單'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** 一列運費明細。到付整列灰掉並把金額畫上刪除線(慣例同運費進貨單的到付列) */
function FreightRow({
  row,
  checked,
  suggested,
  onToggle,
  onPatchTrackingNo,
  patchPending,
  patchSuccess,
  patchError,
}: {
  row: FreightLineItem;
  checked: boolean;
  /** 系統比對出「可能是漏勾的這一筆」時整列標黃 */
  suggested: boolean;
  onToggle: () => void;
  /** 補託運單號。值已 trim;清成空字串是合法的(後端轉成 null),不另外問一次 */
  onPatchTrackingNo: (trackingNo: string) => void;
  patchPending: boolean;
  /** 這一列剛存好 —— 「確認」這個動作的另一半:使用者要看得到它真的進去了 */
  patchSuccess: boolean;
  patchError: unknown;
}): JSX.Element {
  const isCollect = row.paymentType === 'COLLECT';
  const original = row.trackingNo ?? '';
  const [trackingNo, setTrackingNo] = useState(original);

  /**
   * 存失敗就把格子還原成 `row.trackingNo` —— 畫面停在一個其實沒存進去的值,
   * 比什麼都沒發生更糟(下次來看會以為單號補過了)。成功不需要處理:
   * `usePatchFreightLine()` 的 invalidate 會把整份明細重抓。
   */
  useEffect(() => {
    if (patchError) setTrackingNo(original);
  }, [patchError, original]);

  /**
   * 「確認」鈕與 Enter 共用同一條路徑(REQ-20260915002)。**值沒變就不送** ——
   * 這也是確認鈕 disabled 的條件,按不下去就不會有多餘的 API。
   */
  const saveTrackingNo = (): void => {
    const next = trackingNo.trim();
    if (next === original) return;
    setTrackingNo(next);
    onPatchTrackingNo(next);
  };
  const unchanged = trackingNo.trim() === original;

  return (
    <tr
      className={`border-b last:border-0 text-sm ${
        suggested ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-slate-50'
      }`}
      data-testid={`freight-row-${row.id}`}
    >
      <td className="px-4 py-2">
        <input
          type="checkbox"
          aria-label={`選擇 ${row.shipDate} ${row.customerSimpleName}`}
          data-testid={`freight-pick-${row.id}`}
          checked={checked}
          onChange={onToggle}
        />
      </td>
      <td className="px-4 py-2 whitespace-nowrap font-mono">{row.shipDate}</td>
      {/* ⚠️ shipments 沒有自己的單號 —— 顯示的是它所屬訂單的 B 字軌單號 */}
      <td className="px-4 py-2 whitespace-nowrap font-mono">{row.sourceOrderNo}</td>
      {/*
        就地補單號(REQ-20260915001)。出貨紀錄本身仍是 append-only,改的是這筆運費明細。
        送出要按「確認」—— blur 不送(REQ-20260915002):打完字就走開的人,原本會
        以為自己存好了,其實 blur 有沒有觸發他看不出來。
      */}
      <td className="px-4 py-2 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <input
            type="text"
            maxLength={50}
            aria-label={`${row.shipDate} 託運單號`}
            data-testid={`freight-tracking-${row.id}`}
            value={trackingNo}
            disabled={patchPending}
            onChange={(e) => setTrackingNo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveTrackingNo();
              } else if (e.key === 'Escape') {
                setTrackingNo(original);
              }
            }}
            className="border rounded-md px-3 py-2 text-sm font-mono w-64 disabled:bg-slate-100 disabled:text-slate-400"
          />
          <button
            type="button"
            aria-label={`確認 ${row.shipDate} 託運單號`}
            data-testid={`freight-tracking-confirm-${row.id}`}
            onClick={saveTrackingNo}
            disabled={patchPending || unchanged}
            className="px-2 py-1 text-xs rounded border hover:bg-slate-50 disabled:opacity-50"
          >
            確認
          </button>
          <span
            aria-live="polite"
            className={`text-[11px] ${patchPending ? 'text-slate-400' : 'text-emerald-700'}`}
          >
            {patchPending ? '儲存中…' : patchSuccess ? '已儲存' : ''}
          </span>
        </div>
        {patchError ? (
          /* 紅字要能折行 —— 上面那個 nowrap 是為了「儲存中…」不換行,不是為了把錯誤訊息撐成一長條 */
          <div role="alert" className="text-[11px] text-red-600 whitespace-normal">
            {getApiErrorMessage(patchError, '未知錯誤')}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-2">{row.customerSimpleName}</td>
      <td className="px-4 py-2 whitespace-nowrap">
        {FREIGHT_PAYMENT_TYPES[row.paymentType as FreightPaymentType] ?? row.paymentType}
      </td>
      <td className={`px-4 py-2 text-right font-mono ${isCollect ? 'text-slate-400 line-through' : ''}`}>
        {row.amount == null ? '未填' : formatMoney(row.amount)}
      </td>
      <td className="px-4 py-2">
        {row.workOrderState === 'NORMAL' ? (
          <span className="font-mono text-slate-700">{row.salesOrderNo ?? '—'}</span>
        ) : (
          <span className="text-amber-700">
            {FREIGHT_WORK_ORDER_STATES[row.workOrderState as FreightWorkOrderState] ??
              row.workOrderState}
          </span>
        )}
      </td>
    </tr>
  );
}

/** 預設起日 = 本月 1 號(台北)。容器是 UTC,日期一律明確指定時區(CLAUDE.md §6.1) */
function currentPeriodInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
  })
    .format(new Date())
    .slice(0, 7);
}

/** 預設迄日 / 帳單日期 = 台北今天 */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
